/**
 * AcuitySyncHub - the ONE place inside the host dashboard's Clients tab
 * where Acuity data lives. Per spec (Iter 9.49+):
 *   - Shows ALL Acuity clients + ALL Acuity appointments side-by-side.
 *   - Host ticks rows to copy them into Phygital (provider_clients / event_rsvps).
 *   - Names: first_name, last_name, display_name (= first + last). No "name" field.
 *   - Once Acuity is connected, sync stays - no re-auth needed.
 *
 * Mirrors the field copy rules outlined in /app/memory/AIRTABLE_MANUAL.md.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Phone,
  RefreshCw,
  Search,
  User,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const displayNameOf = (first, last, fallback = "") =>
  `${first || ""} ${last || ""}`.trim() || fallback || "(no name)";

export default function AcuitySyncHub({ eventId, connected, onAfterSync, onConnect }) {
  const [activePane, setActivePane] = useState("clients"); // "clients" | "appointments"
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState({});

  const [appointments, setAppointments] = useState([]);
  const [selectedAppointments, setSelectedAppointments] = useState({});

  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.post(
        `${API}/acuity/sync-clients-preview`,
        { event_id: eventId },
        { headers: authHeaders(), withCredentials: true }
      );
      setClients(res.data?.acuity_clients || []);
    } catch {
      toast.error("Could not load Acuity clients");
    }
  }, [eventId]);

  const loadAppointments = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await fetch(`${API}/host/events/${eventId}/acuity-appointments`, {
        headers: { ...authHeaders() },
      });
      const data = await res.json();
      setAppointments(data?.appointments || []);
    } catch {
      toast.error("Could not load Acuity appointments");
    }
  }, [eventId]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadClients(), loadAppointments()]);
    } finally {
      setLoading(false);
    }
  }, [loadClients, loadAppointments]);

  useEffect(() => {
    if (connected) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, eventId]);

  const filteredClients = useMemo(() => {
    const q = search.toLowerCase();
    return clients.filter((c) =>
      !q
        ? true
        : `${c.firstName || ""} ${c.lastName || ""} ${c.email || ""}`
            .toLowerCase()
            .includes(q)
    );
  }, [clients, search]);

  const filteredAppointments = useMemo(() => {
    const q = search.toLowerCase();
    return appointments.filter((a) =>
      !q
        ? true
        : `${a.first_name || ""} ${a.last_name || ""} ${a.email || ""} ${a.type || ""}`
            .toLowerCase()
            .includes(q)
    );
  }, [appointments, search]);

  const importClients = async () => {
    const picked = clients.filter(
      (c) => selectedClients[c.id || c.email] && !c.already_imported
    );
    if (picked.length === 0) {
      toast.error("Pick at least one client to copy over");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/acuity/sync-clients-import`,
        { event_id: eventId, selected_clients: picked },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success(`${res.data.imported} client${res.data.imported === 1 ? "" : "s"} copied into Phygital`);
      setSelectedClients({});
      await loadClients();
      onAfterSync && onAfterSync();
    } catch (e) {
      toast.error("Copy failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const importAppointments = async () => {
    const picked = appointments.filter(
      (a) => selectedAppointments[a.appointment_id] && !a.matched_rsvp_id
    );
    if (picked.length === 0) {
      toast.error("Pick at least one appointment to copy over");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/acuity/sync-appointments-import`,
        { event_id: eventId, selected_appointments: picked },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success(
        `${res.data.imported} appointment${res.data.imported === 1 ? "" : "s"} copied into Phygital`
      );
      setSelectedAppointments({});
      await loadAppointments();
      onAfterSync && onAfterSync();
    } catch (e) {
      toast.error("Copy failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!connected) {
    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="acuity-hub-not-connected">
        <CardContent className="p-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">Acuity is not connected</p>
            <p className="text-xs text-gray-600 mt-1">
              Connect Acuity in Settings - Tech Stack so we can pull your clients, appointments, and services into Phygital.
            </p>
            {onConnect && (
              <Button size="sm" className="mt-3" onClick={onConnect} data-testid="acuity-hub-connect-btn">
                Connect Acuity
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const clientsToPick = filteredClients.filter((c) => !c.already_imported);
  const apptsToPick = filteredAppointments.filter((a) => !a.matched_rsvp_id);
  const selectedClientCount = Object.values(selectedClients).filter(Boolean).length;
  const selectedApptCount = Object.values(selectedAppointments).filter(Boolean).length;

  return (
    <div className="space-y-3" data-testid="acuity-sync-hub">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-semibold">Acuity sync</h3>
          <p className="text-xs text-gray-500">
            Copy your Acuity clients and appointments into Phygital. Once copied, edit them as Phygital records.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={reload} disabled={loading} data-testid="acuity-hub-refresh">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
          <span className="ml-1.5 text-xs">Refresh from Acuity</span>
        </Button>
      </div>

      {/* Pane toggle */}
      <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 text-xs">
        <button
          onClick={() => setActivePane("clients")}
          className={`px-3 py-1.5 rounded-md font-medium ${
            activePane === "clients" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
          }`}
          data-testid="acuity-hub-clients-pane-btn"
        >
          <Users className="w-3.5 h-3.5 inline mr-1.5" />
          Clients ({clients.length})
        </button>
        <button
          onClick={() => setActivePane("appointments")}
          className={`px-3 py-1.5 rounded-md font-medium ${
            activePane === "appointments" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
          }`}
          data-testid="acuity-hub-appointments-pane-btn"
        >
          <Calendar className="w-3.5 h-3.5 inline mr-1.5" />
          Appointments ({appointments.length})
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            activePane === "clients" ? "Search Acuity clients..." : "Search appointments..."
          }
          className="pl-8 h-9 text-sm"
          data-testid="acuity-hub-search"
        />
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : activePane === "clients" ? (
        <div className="space-y-2" data-testid="acuity-hub-clients-list">
          {clientsToPick.length > 0 && (
            <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={
                    selectedClientCount > 0 && selectedClientCount === clientsToPick.length
                  }
                  onCheckedChange={(v) => {
                    const next = {};
                    if (v) clientsToPick.forEach((c) => (next[c.id || c.email] = true));
                    setSelectedClients(next);
                  }}
                  data-testid="acuity-hub-clients-toggle-all"
                />
                <span className="text-xs text-gray-600">
                  {selectedClientCount} of {clientsToPick.length} selected
                </span>
              </div>
              <Button
                size="sm"
                onClick={importClients}
                disabled={saving || selectedClientCount === 0}
                data-testid="acuity-hub-clients-copy-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Copy {selectedClientCount || ""} into Phygital
              </Button>
            </div>
          )}
          {filteredClients.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              {clients.length === 0
                ? "No clients in your Acuity account yet."
                : "No matches for that search."}
            </p>
          ) : (
            filteredClients.map((c) => {
              const id = c.id || c.email;
              const dn = displayNameOf(c.firstName, c.lastName, c.email);
              return (
                <Card
                  key={id}
                  className={c.already_imported ? "opacity-60" : "hover:bg-gray-50"}
                  data-testid={`acuity-hub-client-${id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <Checkbox
                      checked={!!selectedClients[id]}
                      disabled={c.already_imported}
                      onCheckedChange={(v) =>
                        setSelectedClients((s) => ({ ...s, [id]: v }))
                      }
                    />
                    <User className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{dn}</p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-3 flex-wrap">
                        {c.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {c.email}
                          </span>
                        )}
                        {c.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {c.phone}
                          </span>
                        )}
                      </p>
                    </div>
                    {c.already_imported && (
                      <Badge
                        variant="outline"
                        className="text-[9px] bg-emerald-50 border-emerald-200 text-emerald-700"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                        Copied
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="acuity-hub-appointments-list">
          {apptsToPick.length > 0 && (
            <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={
                    selectedApptCount > 0 && selectedApptCount === apptsToPick.length
                  }
                  onCheckedChange={(v) => {
                    const next = {};
                    if (v) apptsToPick.forEach((a) => (next[a.appointment_id] = true));
                    setSelectedAppointments(next);
                  }}
                  data-testid="acuity-hub-appointments-toggle-all"
                />
                <span className="text-xs text-gray-600">
                  {selectedApptCount} of {apptsToPick.length} selected
                </span>
              </div>
              <Button
                size="sm"
                onClick={importAppointments}
                disabled={saving || selectedApptCount === 0}
                data-testid="acuity-hub-appointments-copy-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Copy {selectedApptCount || ""} into Phygital
              </Button>
            </div>
          )}
          {filteredAppointments.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">
              {appointments.length === 0
                ? "No upcoming appointments in your Acuity account."
                : "No matches for that search."}
            </p>
          ) : (
            filteredAppointments.map((a) => {
              const dn =
                a.display_name || displayNameOf(a.first_name, a.last_name, a.email);
              return (
                <Card
                  key={a.appointment_id}
                  className={a.matched_rsvp_id ? "opacity-60" : "hover:bg-gray-50"}
                  data-testid={`acuity-hub-appointment-${a.appointment_id}`}
                >
                  <CardContent className="p-3 flex items-center gap-3">
                    <Checkbox
                      checked={!!selectedAppointments[a.appointment_id]}
                      disabled={!!a.matched_rsvp_id}
                      onCheckedChange={(v) =>
                        setSelectedAppointments((s) => ({ ...s, [a.appointment_id]: v }))
                      }
                    />
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{dn}</p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {fmtDate(a.datetime)}
                        </span>
                        {a.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {a.email}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-gray-600 mt-0.5 truncate">{a.type}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {a.canceled && (
                        <Badge variant="destructive" className="text-[9px]">
                          Canceled
                        </Badge>
                      )}
                      {a.paid && (
                        <Badge className="text-[9px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Paid
                        </Badge>
                      )}
                      {a.matched_rsvp_id && (
                        <Badge
                          variant="outline"
                          className="text-[9px] bg-emerald-50 border-emerald-200 text-emerald-700"
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                          Copied
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
